/**
 *  Wrapper for googletag functionality
 */
const breakpoints = require('../../services/breakpoints'),
	slotStates = require('../domain/slotStates'),
	SlotRenderData = require('../domain/slotRenderData'),
	querystring = require('../../utils/querystring'),
	pubsub = require('../../utils/pubsub'),
	perf = require('../../utils/performance');

/* Prep googletag */
window.googletag = window.googletag || {};
window.googletag.cmd = window.googletag.cmd || [];

let waitingForRenderEnded = [],
	batchedRefreshCalls = [],
	customRefreshCallback,
	requestNonPersonalizedAds = true;

_addEventListeners();

function init(/** Settings */ settings) {
	customRefreshCallback = settings.customRefreshCallback;
	requestNonPersonalizedAds = typeof settings.requestNonPersonalizedAds !== 'undefined' && !settings.requestNonPersonalizedAds;

	return new Promise((resolve) => {
		perf.mark('dfp - googletag.cmd wait - start');
		window.googletag.cmd.push(() => {
			perf.mark('dfp - googletag.cmd wait - end');

			perf.mark('dfp - services - configure - start');

			_clean();
			_configureServices(settings);

			perf.mark('dfp - services - configure - end');
			resolve();
		});
	});
}

function _clean() {
	window.googletag.destroySlots();
	window.googletag.pubads().clearTargeting();
}

function _configureServices(/** Settings */ settings) {
	window.googletag.pubads().enableSingleRequest();
	window.googletag.pubads().disableInitialLoad();

	if (typeof settings.collapseEmpty === 'undefined' || settings.collapseEmpty) {
		window.googletag.pubads().collapseEmptyDivs();
	}

	if (settings.ppid) {
		window.googletag.pubads().setPublisherProvidedId(settings.ppid);
	}

	window.googletag.pubads().setRequestNonPersonalizedAds(typeof settings.requestNonPersonalizedAds === 'undefined' || settings.requestNonPersonalizedAds ? 1 : 0);

	window.googletag.pubads().setSafeFrameConfig({
		'allowOverlayExpansion': true,
		'allowPushExpansion': true,
		'sandbox': true
	});

	_configureGlobalTargeting(settings.targeting);

	window.googletag.enableServices();
}

function _configureGlobalTargeting(pageTargeting) {
	const targeting = _getGlobalTargeting(pageTargeting);

	Object.keys(targeting).forEach((t) => {
		window.googletag.pubads().setTargeting(t, targeting[t]);
	});
}

function _getGlobalTargeting(pageTargeting) {
	const targeting = Object.assign({}, pageTargeting);

	if (querystring.get('adops')) {
		targeting.adops = querystring.get('adops');
	}

	targeting.targeted = requestNonPersonalizedAds;
	targeting.screen = breakpoints.getCurrentBreakpoint();

	return Object.keys(targeting).reduce((agg, t) => {
		const value = _getSafeTargetValue(targeting[t]);

		if (value !== null) {
			agg[t] = value;
		}

		return agg;
	}, {});
}

function _getSafeTargetValue(value) {
	if (typeof value === 'string' || value === null) {
		return value;
	}

	if (typeof value === 'boolean' || typeof value === 'number') {
		return value.toString();
	}

	if (Array.isArray(value) && value.length > 0) {
		return value.map((val) => {
			if (Array.isArray(val)) {
				return null;
			}

			return _getSafeTargetValue(val);
		}).filter((val) => val !== null);
	}

	console.warn('[ADVERT] Targeting values should be a boolean, number, string, or non-empty array. Ignoring invalid value: ', value);

	return null;
}

function _addEventListeners() {
	pubsub.subscribe('breakpoint.changed', _onBreakpointChange);

	window.googletag.cmd.push(() => {
		window.googletag.pubads().addEventListener('slotRenderEnded', _onSlotRenderEnded);
	});
}

function onSlotVisibilityChanged(fn) {
	window.googletag.pubads().addEventListener('slotVisibilityChanged', fn);
}

function onImpressionViewable(fn) {
	window.googletag.pubads().addEventListener('impressionViewable', fn);
}

function defineSlot(/** Slot */ slot) {
	return new Promise((resolve) => {
		const gtag = slot.outOfPage ? _createOutOfPageSlot(slot) : _createSlot(slot);

		_addTargeting(gtag, slot.targeting);

		gtag.addService(window.googletag.pubads());

		resolve(gtag);
	});
}

function _createOutOfPageSlot(/** Slot */ slot) {
	return window.googletag.defineOutOfPageSlot(slot.getAdUnit(), slot.domId);
}

function _createSlot(/** Slot */ slot) {
	const bps = breakpoints.getBreakpoints(),
		sizeMapping = slot.getSizeMapping(),
		gmap = window.googletag.sizeMapping();

	Object.keys(bps).forEach((bp) => {
		gmap.addSize([bps[bp], 0], sizeMapping[bp] || []);
	});

	return window.googletag.defineSlot(slot.getAdUnit(), 'fluid', slot.domId)
		.defineSizeMapping(gmap.build());
}

function _addTargeting(gtag, targeting = {}, removePrevious = false) {
	if (removePrevious) {
		gtag.clearTargeting();
	}

	Object.keys(targeting).forEach((t) => {
		const value = _getSafeTargetValue(targeting[t]);

		if (value !== null) {
			gtag.setTargeting(t, value);
		}
	});
}

function loadSlot(/** Slot */ slot) {
	return new Promise((resolve, reject) => {
		if (!slot.gtag) {
			return reject(new Error(`Tried loading slot '${slot.name}', but it doesn't have a googletag slot yet.`));
		}

		if (slot.state !== slotStates.DEFINED) {
			return reject(new Error(`Tried loading slot '${slot.name}', but it isn't currently defined. Current state: ${slot.state}`));
		}

		window.googletag.display(slot.gtag);
		resolve();
	});
}

async function renderSlot(/** Slot */ slot) {
	if (!slot.gtag) {
		throw new Error(`Tried rendering slot '${slot.name}', but it doesn't have a googletag slot yet.`);
	}

	if (slot.state !== slotStates.LOADED) {
		throw new Error(`Tried rendering slot '${slot.name}', but it isn't currently loaded. Current state: ${slot.state}`);
	}

	return (await refreshSlots([slot], false))[0][1];
}

function _onSlotRenderEnded(e) {
	waitingForRenderEnded = waitingForRenderEnded.filter((w) => {
		if (w.slot.domId === e.slot.getSlotElementId()) {
			w.resolve([w.slot, new SlotRenderData(e)]);

			return false;
		}

		return true;
	});
}

function _onBreakpointChange(currentBreakpoint) {
	window.googletag.pubads().setTargeting('screen', currentBreakpoint);
}

function destroySlot(/** Slot*/ slot) {
	return new Promise((resolve) => {
		window.googletag.destroySlots([slot.gtag]);

		resolve();
	});
}

async function refreshSlots(/** Slot[] */ slots, changeCorrelator = true) {
	const waitForRenders = Promise.all(slots.map(_waitForRender));
	let slotsToRefresh = slots;

	slotsToRefresh.forEach((s) => {
		_addTargeting(s.gtag, s.targeting);
	});

	if (typeof customRefreshCallback === 'function') {
		const allCallbackResults = await Promise.all(slotsToRefresh
			.map(async function(s) {
				try {
					perf.mark(`dfp - customRefreshCallback - ${s.name} - start`);

					return await customRefreshCallback(s);
				} catch (e) {
					console.error('[ADVERT] Something went wrong calling customRefreshCallback', e);

					return true;
				} finally {
					perf.mark(`dfp - customRefreshCallback - ${s.name} - end`);
				}
			}));

		slotsToRefresh = slotsToRefresh.filter((_, idx) => {
			return allCallbackResults[idx] !== false;
		});
	}

	_addToBatchRequests(slotsToRefresh, {
		changeCorrelator
	});

	return waitForRenders;
}

function _addToBatchRequests(slots, options) {
	if (batchedRefreshCalls.length === 0) {
		Promise.resolve().then(_doBatchRefresh); // Shorter delay than setTimeout 0 because it's a micro task
	}

	batchedRefreshCalls.push({
		slots,
		options
	});
}

function _doBatchRefresh() {
	_groupByOptions(batchedRefreshCalls).forEach((callGroup) => {
		const orderedSlots = _orderAsDefined(callGroup.slots);

		perf.mark('dfp - batch refresh');

		window.googletag.pubads().refresh(orderedSlots.map(s => s.gtag), callGroup.options);
	});

	batchedRefreshCalls = [];
}

function _groupByOptions(arr) {
	const knownOptions = [];

	return arr.reduce((agg, call) => {
		const optionHash = JSON.stringify(call.options),
			hashPos = knownOptions.indexOf(optionHash);

		if (hashPos === -1) {
			knownOptions.push(optionHash);
			agg.push({
				'slots': call.slots,
				'options': call.options
			});
		} else {
			agg[hashPos].slots.push(...call.slots);
		}

		return agg;
	}, []);
}

function _orderAsDefined(slots) {
	const definedSlots = window.googletag.pubads().getSlots(),
		sorted = [];

	slots.forEach((slot) => {
		const pos = definedSlots.indexOf(slot.gtag);

		if (pos !== -1) {
			sorted[pos] = slot;
		}
	});

	return sorted.filter(s => s);
}

function _waitForRender(slot) {
	return new Promise((resolve, reject) => {
		waitingForRenderEnded.push({
			slot,
			resolve,
			reject
		});
	});
}

function getAppliedTargeting(slot) {
	const targeting = {};

	window.googletag.pubads().getTargetingKeys().forEach((targetingKey) => {
		targeting[targetingKey] = window.googletag.pubads().getTargeting(targetingKey).join(',');
	});

	if (slot && slot.gtag) {
		slot.gtag.getTargetingKeys().forEach((targetingKey) => {
			targeting[targetingKey] = slot.gtag.getTargeting(targetingKey).join(',');
		});
	}

	return targeting;
}

function updatePageTargeting(pageTargeting) {
	window.googletag.pubads().clearTargeting();

	_configureGlobalTargeting(pageTargeting);

	pubsub.publish('settings.changed');
}

module.exports = {
	init,
	defineSlot,
	loadSlot,
	renderSlot,
	destroySlot,
	refreshSlots,
	onSlotVisibilityChanged,
	onImpressionViewable,
	getAppliedTargeting,
	updatePageTargeting
};
